import { useEffect, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { useAuth } from "../../context/AuthContext";
import { profileService } from "../../services/profileService";
import { toast } from "react-hot-toast";
import { LedgerField, LedgerSection } from "./LedgerSection";

// Sovereign Emerald palette (chips only — layout tokens live in LedgerSection)
const SE = {
  bottle:        '#1F5A47',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  hairline:      'rgba(11,59,46,0.14)',
  hoverWash:     'rgba(11,59,46,0.05)',
  muted:         '#7C8A82',
};

export default function UserInfoCard() {
  const { isOpen, openModal, closeModal } = useModal();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);

  const buildFormData = () => ({
    firstName: user?.firstName || user?.name?.split(" ")[0] || "",
    lastName:  user?.lastName  || user?.name?.split(" ").slice(1).join(" ") || "",
    email:     user?.email     || "",
    phone:     user?.phone     || user?.mobile || "",
    bio:       user?.bio       || user?.description || "",
    facebook:  user?.socialLinks?.facebook  || "",
    twitter:   user?.socialLinks?.twitter   || user?.socialLinks?.x || "",
    linkedin:  user?.socialLinks?.linkedin  || "",
    instagram: user?.socialLinks?.instagram || "",
  });

  const [formData, setFormData] = useState(buildFormData());

  useEffect(() => {
    if (isOpen) setFormData(buildFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await profileService.updateProfile({
        firstName: formData.firstName,
        lastName:  formData.lastName,
        email:     formData.email,
        phone:     formData.phone,
        bio:       formData.bio,
        socialLinks: {
          facebook:  formData.facebook,
          twitter:   formData.twitter,
          linkedin:  formData.linkedin,
          instagram: formData.instagram,
        },
      });
      if (result.success) {
        toast.success("Personal information updated");
        if (result.data) {
          updateUser(result.data);
        } else {
          updateUser({
            firstName: formData.firstName,
            lastName:  formData.lastName,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            email:  formData.email,
            phone:  formData.phone,
            bio:    formData.bio,
            socialLinks: { facebook: formData.facebook, twitter: formData.twitter, linkedin: formData.linkedin, instagram: formData.instagram },
          });
        }
        closeModal();
      } else { toast.error(result.message); }
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const userEmail = user?.email || "";
  const userPhone = user?.phone || user?.mobile || "";
  const userBio   = user?.bio   || user?.description || "";

  const departmentRoleList: Array<{ department: string; roleName: string; active: boolean }> = (() => {
    if (Array.isArray(user?.roles) && user!.roles.length > 0) {
      return user!.roles.map((r: any) => ({
        department: r.department,
        roleName:
          (typeof r.roleId === "object" ? r.roleId?.name : null) ||
          user?.role?.name || user?.roleName || "User",
        active: r.department === user?.activeDepartment,
      }));
    }
    return [{ department: user?.activeDepartment || "", roleName: user?.role?.name || user?.role || "User", active: true }];
  })();

  return (
    <>
      <LedgerSection
        title="Personal Information"
        subtitle="Your public profile and contact details"
        onEdit={openModal}
      >
        <LedgerField label="First Name" value={user?.firstName || user?.name?.split(" ")[0] || ""} />
        <LedgerField label="Last Name"  value={user?.lastName || user?.name?.split(" ").slice(1).join(" ") || ""} />
        <LedgerField label="Email Address" value={userEmail} />
        <LedgerField label="Phone" value={userPhone} />
        <LedgerField
          fullWidth
          label={departmentRoleList.length > 1 ? "Department Roles" : "Role"}
          value={
            <div className="mt-0.5 flex flex-wrap gap-1.5">
              {departmentRoleList.map((dr, idx) => (
                <span
                  key={`${dr.department}-${idx}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold tracking-[0.07em] uppercase"
                  style={
                    dr.active
                      ? { backgroundColor: SE.champagneWash, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }
                      : { backgroundColor: SE.hoverWash, color: SE.muted, border: `1px solid ${SE.hairline}` }
                  }
                >
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: dr.active ? SE.champagne : SE.muted }} />
                  {dr.roleName}
                  {dr.department && <span className="opacity-60">· {dr.department}</span>}
                </span>
              ))}
            </div>
          }
        />
        <LedgerField fullWidth label="Bio" value={userBio} />
        {(user?.socialLinks?.facebook || user?.socialLinks?.twitter || user?.socialLinks?.x || user?.socialLinks?.linkedin || user?.socialLinks?.instagram) && (
          <LedgerField
            fullWidth
            label="Social Links"
            value={
              <div className="mt-0.5 flex flex-wrap gap-2">
                {[
                  { key: "linkedin",  url: user?.socialLinks?.linkedin },
                  { key: "twitter",   url: user?.socialLinks?.twitter || user?.socialLinks?.x },
                  { key: "facebook",  url: user?.socialLinks?.facebook },
                  { key: "instagram", url: user?.socialLinks?.instagram },
                ]
                  .filter((s) => !!s.url)
                  .map((s) => (
                    <a
                      key={s.key}
                      href={s.url as string}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] transition-all"
                      style={{ backgroundColor: SE.champagneTile, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile; }}
                    >
                      <span className="font-semibold capitalize">{s.key}</span>
                      <span className="max-w-[180px] truncate" style={{ color: SE.muted }}>
                        {(s.url as string).replace(/^https?:\/\/(www\.)?/, "")}
                      </span>
                    </a>
                  ))}
              </div>
            }
          />
        )}
      </LedgerSection>

      {/* ── EDIT MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[700px] m-4">
        <div className="no-scrollbar relative w-full max-w-[700px] overflow-y-auto rounded-3xl bg-white p-4 dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              Edit Personal Information
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              Update your details to keep your profile up-to-date.
            </p>
          </div>
          <form className="flex flex-col" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="custom-scrollbar h-[450px] overflow-y-auto px-2 pb-3">
              <div>
                <h5 className="mb-5 text-lg font-medium text-gray-800 dark:text-white/90 lg:mb-6">Personal Information</h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div className="col-span-2 lg:col-span-1">
                    <Label>First Name</Label>
                    <Input type="text" value={formData.firstName} onChange={(e) => handleInputChange("firstName", e.target.value)} placeholder="Enter first name" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Last Name</Label>
                    <Input type="text" value={formData.lastName} onChange={(e) => handleInputChange("lastName", e.target.value)} placeholder="Enter last name" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Email Address</Label>
                    <Input type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} placeholder="Enter email address" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Phone</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="Enter phone number" />
                  </div>
                  <div className="col-span-2">
                    <Label>Bio</Label>
                    <Input type="text" value={formData.bio} onChange={(e) => handleInputChange("bio", e.target.value)} placeholder="Tell us about yourself" />
                  </div>
                </div>
              </div>
              <div className="mt-7">
                <h5 className="mb-5 text-lg font-medium text-gray-800 dark:text-white/90 lg:mb-6">Social Links</h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div><Label>LinkedIn</Label><Input type="text" value={formData.linkedin} onChange={(e) => handleInputChange("linkedin", e.target.value)} placeholder="https://www.linkedin.com/in/yourprofile" /></div>
                  <div><Label>X.com</Label><Input type="text" value={formData.twitter} onChange={(e) => handleInputChange("twitter", e.target.value)} placeholder="https://x.com/yourprofile" /></div>
                  <div><Label>Facebook</Label><Input type="text" value={formData.facebook} onChange={(e) => handleInputChange("facebook", e.target.value)} placeholder="https://www.facebook.com/yourprofile" /></div>
                  <div><Label>Instagram</Label><Input type="text" value={formData.instagram} onChange={(e) => handleInputChange("instagram", e.target.value)} placeholder="https://instagram.com/yourprofile" /></div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
              <Button size="sm" variant="outline" onClick={closeModal} disabled={saving}>Close</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} type="submit">{saving ? "Saving…" : "Save Changes"}</Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
